import { DataSetSubmissionDetailRoutePath } from "#routes/data-tools/submissions/datasets/[transactionId]/detail/index";
import {
  PillToggleVariants,
  type PillProps,
  type PillToggleProps,
  type StepType,
} from "@validereinc/common-components";
import {
  DatasetType,
  TransactionDetailItemStatus,
  TransactionDetailItemStatusType,
  TransactionDetailsItemType,
  TransactionDetailsType,
  TransactionType,
} from "@validereinc/domain";
import {
  datetimeFormatter,
  toFlattenedObject,
  toTitleCaseString,
} from "@validereinc/utilities";

export const getTransactionTitle = (
  transaction?: TransactionType,
  dataset?: DatasetType
) =>
  dataset?.name && transaction?.created_at
    ? `${toTitleCaseString(dataset.name, { intent: "content" })} - ${datetimeFormatter(new Date(transaction.created_at))}`
    : DataSetSubmissionDetailRoutePath.title;

export const getStepStatusFromState = (
  trx?: TransactionType
): StepType["intent"] => {
  switch (trx?.state) {
    case "success":
      return "success";
    case "partial_success":
      return "warning";
    case "partial_failure":
    case "failed":
      return "error";
    case "pending":
      return "pending";
    case "retried":
    default:
      return "default";
  }
};

export const getTransactionDetailStatusPillVariant = (
  status?: TransactionDetailItemStatusType
): PillProps["variant"] => {
  switch (status) {
    case TransactionDetailItemStatus.CREATED:
    case TransactionDetailItemStatus.SUCCESSFUL:
      return "success";
    case TransactionDetailItemStatus.UPDATED:
      return "info";
    case TransactionDetailItemStatus.FAILED:
      return "error";
    case TransactionDetailItemStatus.UNCHANGED:
    default:
      return "default";
  }
};

export const getTransactionDetailStatusPillToggleVariant = (
  status?: TransactionDetailItemStatusType
): PillToggleProps["variant"] => {
  switch (status) {
    case TransactionDetailItemStatus.CREATED:
    case TransactionDetailItemStatus.SUCCESSFUL:
      return PillToggleVariants.GOOD;
    case TransactionDetailItemStatus.UPDATED:
      return PillToggleVariants.PENDING;
    case TransactionDetailItemStatus.FAILED:
      return PillToggleVariants.FAILURE;
    case TransactionDetailItemStatus.UNCHANGED:
      return PillToggleVariants.NEUTRAL;
    default:
      return;
  }
};

export const extractItemsFromTransactionDetails = (queryData?: {
  data: TransactionDetailsType[];
}): TransactionDetailsItemType[] => queryData?.data?.[0]?.details ?? [];

/** We get a pseudo-JSON representation of the submitted data; clean it up a bit to parse it */
export const extractTransactionItemData = (data: string) => {
  const cleanedData = data
    .replaceAll("'", '"')
    .replaceAll("None", "null")
    .replaceAll("True", "true")
    .replaceAll("False", "false");

  try {
    return Object.fromEntries(
      Object.entries(toFlattenedObject(JSON.parse(cleanedData))).map(
        ([key, value]) => [
          key,
          typeof value === "string" || !value ? value : JSON.stringify(value),
        ]
      )
    );
  } catch (e) {
    console.error(`Cannot extract data from ${data}: ${e}.`);
    // Fail gracefully
    return {};
  }
};
